import React from 'react';
import { useKioskStore } from '@/store/kiosk-store';
import { useSession } from '@/hooks/use-session';

/**
 * Attract loop screen — shown when no session is active.
 * Adapted from Lovable WelcomeScreen. No react-router-dom.
 * Touch/click anywhere to start a session.
 */
const AttractScreen: React.FC = () => {
  const config = useKioskStore((s) => s.config);
  const { startSession } = useSession();

  // Institution-wide display name (e.g. "City Art Museum"), sourced from
  // branding — deliberately NOT config.location, which is the per-kiosk
  // physical descriptor (e.g. "North Wing Entrance").
  const institutionName = config?.branding.location_name ?? 'The Museum';
  const welcomeMessage =
    config?.branding.welcome_message ?? "Welcome — We're glad you're here";

  const handleInteraction = () => {
    startSession();
  };

  return (
    <button
      onClick={handleInteraction}
      className="flex flex-col items-center justify-center h-screen w-screen bg-background cursor-pointer focus:outline-none"
      aria-label="Tap anywhere to continue"
      style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
    >
      <div className="flex flex-col items-center gap-8 animate-kiosk-fade-in">
        {/* Logo / Icon */}
        <div className="flex items-center justify-center w-28 h-28 rounded-3xl bg-primary shadow-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-14 h-14 text-primary-foreground"
            aria-hidden="true"
          >
            <path d="M2 20h20" />
            <path d="M5 20V8l7-5 7 5v12" />
            <path d="M9 20v-6h6v6" />
            <path d="M9 12h.01" />
            <path d="M15 12h.01" />
          </svg>
        </div>

        {/* Institution name + welcome message */}
        <div className="text-center space-y-3">
          <h1 className="text-5xl font-bold text-foreground tracking-tight">{institutionName}</h1>
          <p className="text-xl text-default-500 font-light">{welcomeMessage}</p>
        </div>
      </div>

      {/* Pulsing call-to-action */}
      <div className="absolute bottom-16 animate-pulse">
        <p className="text-base text-default-400 font-medium tracking-wide">
          Touch anywhere to begin
        </p>
      </div>
    </button>
  );
};

export default AttractScreen;
